// Pure helpers for the admin Protections controls (device card + fleet-defaults card).

export const DELAY_KEY = 'hdmi_in_usb_audio_delay_ms';
export const DELAY_MIN = 0;
export const DELAY_MAX = 300;

export const FLAG_META = {
  audio_health_recovery:  { name: 'Audio auto-repair',          desc: 'Repairs USB audio faults automatically' },
  rtdb_write_watchdog:    { name: 'Realtime write watchdog',    desc: 'Detects stalled realtime-database writes and recovers' },
  rest_fallback_on_stall: { name: 'REST fallback on stall',     desc: 'Falls back to REST updates when the realtime connection stalls' },
  change_only_writes:     { name: 'Change-only writes',         desc: 'Skips realtime writes when nothing has changed' },
  action_dedup:           { name: 'Command de-duplication',     desc: 'Ignores duplicate commands replayed from the queue' },
  audio_probe:            { name: 'Audio probe',                desc: 'Silent AudioTrack probe. Off = zero probe tracks; Observe = periodic probes only, no reaction; On = also ratio-triggered and post-recovery probes (rate-limited)' },
  playback_watchdog:      { name: 'Playback watchdog',          desc: 'Detects playback whose position stopped advancing (incl. a YouTube item stuck buffering). Observe = events only (the flag-free frozen-item skip keeps working as before); On = the freeze ladder: nudge +2 s, nudge +15 s, reload at the position, rebuild the player, skip, give up (re-armed after 10 min) - capped per hour, never during prayer / pause / mute. Needs player 413 or newer for the full ladder' },
  device_reboot_on_exhausted: { name: 'Reboot when audio exhausted', desc: 'Lets the SERVER reboot a rooted device (the existing restart_device message) when its audio pipeline is still exhausted after the 10-minute re-check (an app restart does not fix it). At most once per 6 h. Off by default' },
  network_recovery:       { name: 'Network recovery',          desc: 'After the network returns: reloads the current item if playback did not resume (3 bounded attempts, never during an audio-stall repair) and re-opens the realtime command channel if it stays down while REST works. Observe = logs what it would do. On by default' },
  network_self_heal:      { name: 'Network self-heal',          desc: 'Rooted units only: turns a firmware-disabled Wi-Fi back on (once), and restarts the device after a network fault it could not heal (Wi-Fi did not return, or YouTube still rebuffering 5 min after one reload). At most one automatic restart per 6 h. Observe only by default — logs exactly what it would do with the measured numbers' },
  audio_keepalive:        { name: 'USB audio keep-alive',      desc: 'While the app is in the foreground and the output is a USB audio device, holds ONE silent app-owned audio track so the output is not put to standby during a long rebuffer (hypothesis: the reopen after standby is where the USB sink stalls). Released before an audio-server restart. Observe = logs what it would do, creates no track. On by default' },
  [DELAY_KEY]:            { name: 'HDMI-IN audio delay (USB)',  desc: 'Delays audio on the USB speaker while showing HDMI-IN, in ms (0–300)' },
};

export const FLAG_ORDER = ['audio_health_recovery', 'rtdb_write_watchdog', 'rest_fallback_on_stall', 'change_only_writes', 'action_dedup', 'audio_probe', 'playback_watchdog', 'device_reboot_on_exhausted', 'network_recovery', 'network_self_heal', 'audio_keepalive'];

export const VALUE_LABEL = { on: 'On', observe_only: 'Observe only', off: 'Off' };
export const VALUE_VARIANT = { on: 'teal', observe_only: 'gold', off: 'danger' };

export function flagName(key) { return FLAG_META[key]?.name ?? key; }

/** Source label. In the global card an override is "global override"; in the device card it is "device override". */
export function sourceLabel(source, scope) {
  if (source === scope) return `${scope} override`;
  if (source === 'device') return 'device override';
  if (source === 'company') return 'company';
  if (source === 'global') return 'global';
  return 'default';
}

/** null when valid, else a message. Integers 0–300 only (no decimals, no exponent, no sign). */
export function validateDelay(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return 'Enter a value between 0 and 300.';
  if (!/^\d+$/.test(s)) return 'Whole numbers only (0–300).';
  const n = Number(s);
  if (n < DELAY_MIN || n > DELAY_MAX) return `Value must be between ${DELAY_MIN} and ${DELAY_MAX} ms.`;
  return null;
}

/** First message from a Laravel 422 body, else the generic message. */
export function apiErrorMessage(err, fallback = 'Request failed.') {
  const data = err?.response?.data;
  const first = data?.errors && Object.values(data.errors)[0];
  return (Array.isArray(first) ? first[0] : first) || data?.message || fallback;
}

/** Audit old/new value for display; null means "inherited" (no override). */
export function auditValue(flag, v) {
  if (v === null || v === undefined) return 'inherited';
  if (flag === DELAY_KEY) return `${v} ms`;
  return VALUE_LABEL[v] ?? String(v);
}

/**
 * Normalises the two backend shapes to one list of rows:
 * [{ key, kind: 'flag'|'delay', value, source, def, overridden }]
 * - device scope: GET /admin/protections/device/{id}  ({flags:{k:{value,source,default}}, settings:{k:{...}}})
 * - global scope: GET /admin/protections  (defaults + raw overrides) — effective = global override else default.
 */
export function buildRows(scope, payload) {
  const rows = [];
  if (scope === 'device') {
    const flags = payload?.flags ?? {};
    const keys = [...FLAG_ORDER.filter(k => k in flags), ...Object.keys(flags).filter(k => !FLAG_ORDER.includes(k))];
    keys.forEach(k => rows.push({ key: k, kind: 'flag', value: flags[k].value, source: flags[k].source, def: flags[k].default, overridden: flags[k].source === 'device' }));
    const d = payload?.settings?.[DELAY_KEY];
    if (d) rows.push({ key: DELAY_KEY, kind: 'delay', value: Number(d.value), source: d.source, def: d.default, overridden: d.source === 'device' });
    return rows;
  }
  const defaults = payload?.defaults ?? {};
  const overrides = (payload?.overrides ?? []).filter(o => o.scope === 'global');
  const byFlag = Object.fromEntries(overrides.map(o => [o.flag, o.value]));
  const keys = [...FLAG_ORDER.filter(k => k in defaults), ...(payload?.flags ?? []).filter(k => !FLAG_ORDER.includes(k) && k in defaults)];
  keys.forEach(k => {
    const has = k in byFlag;
    rows.push({ key: k, kind: 'flag', value: has ? byFlag[k] : defaults[k], source: has ? 'global' : 'default', def: defaults[k], overridden: has });
  });
  const cfg = payload?.numeric_settings?.[DELAY_KEY];
  if (cfg) {
    const has = DELAY_KEY in byFlag;
    rows.push({ key: DELAY_KEY, kind: 'delay', value: has ? Number(byFlag[DELAY_KEY]) : Number(cfg.default), source: has ? 'global' : 'default', def: Number(cfg.default), overridden: has });
  }
  return rows;
}
